<template>
    <!-- Skeleton Loaders -->
    <div v-if="type === 'skeleton-card'" class="skeleton-card">
        <div class="skeleton-image"></div>
        <div class="skeleton-content">
            <div class="skeleton-title"></div>
            <div class="skeleton-text"></div>
            <div class="skeleton-text short"></div>
            <div class="skeleton-button"></div>
        </div>
    </div>

    <div v-else-if="type === 'skeleton-list'" class="skeleton-list">
        <div v-for="n in count" :key="n" class="skeleton-list-item">
            <div class="skeleton-avatar"></div>
            <div class="skeleton-list-content">
                <div class="skeleton-text"></div>
                <div class="skeleton-text short"></div>
            </div>
        </div>
    </div>

    <div v-else-if="type === 'skeleton-table'" class="skeleton-table">
        <div class="skeleton-table-header">
            <div v-for="n in 4" :key="n" class="skeleton-text"></div>
        </div>
        <div v-for="n in count" :key="n" class="skeleton-table-row">
            <div v-for="m in 4" :key="m" class="skeleton-text"></div>
        </div>
    </div>

    <div v-else-if="type === 'skeleton-form'" class="skeleton-form">
        <div v-for="n in count" :key="n" class="skeleton-form-field">
            <div class="skeleton-label"></div>
            <div class="skeleton-input"></div>
        </div>
        <div class="skeleton-button-group">
            <div class="skeleton-button"></div>
            <div class="skeleton-button secondary"></div>
        </div>
    </div>

    <!-- Empty States -->
    <div v-else-if="type === 'empty-recipes'" class="empty-state">
        <div class="empty-icon">🍳</div>
        <h3 class="empty-title">No recipes yet</h3>
        <p class="empty-description">
            Start building your recipe collection by creating your first recipe.
        </p>
        <button class="btn-primary" @click="$emit('action')">
            Create Your First Recipe
        </button>
    </div>

    <div v-else-if="type === 'empty-meals'" class="empty-state">
        <div class="empty-icon">🍽️</div>
        <h3 class="empty-title">No meals planned</h3>
        <p class="empty-description">
            Plan your meals for the week to stay organized and eat well.
        </p>
        <button class="btn-primary" @click="$emit('action')">
            Plan Your First Meal
        </button>
    </div>

    <div v-else-if="type === 'empty-shopping-lists'" class="empty-state">
        <div class="empty-icon">🛒</div>
        <h3 class="empty-title">No shopping lists</h3>
        <p class="empty-description">
            Create shopping lists to organize your grocery trips and never forget an ingredient.
        </p>
        <button class="btn-primary" @click="$emit('action')">
            Create Shopping List
        </button>
    </div>

    <div v-else-if="type === 'empty-ingredients'" class="empty-state">
        <div class="empty-icon">🥕</div>
        <h3 class="empty-title">No ingredients found</h3>
        <p class="empty-description">
            Add ingredients to your pantry to start building recipes and meal plans.
        </p>
        <button class="btn-primary" @click="$emit('action')">
            Add Ingredients
        </button>
    </div>

    <div v-else-if="type === 'empty-search'" class="empty-state">
        <div class="empty-icon">🔍</div>
        <h3 class="empty-title">No results found</h3>
        <p class="empty-description">
            Try adjusting your search terms or filters to find what you're looking for.
        </p>
        <button class="btn-secondary" @click="$emit('clear-search')">
            Clear Search
        </button>
    </div>

    <!-- Error States -->
    <div v-else-if="type === 'error-network'" class="error-state">
        <div class="error-icon">📡</div>
        <h3 class="error-title">Connection Problem</h3>
        <p class="error-description">
            We're having trouble connecting to our servers. Please check your internet connection and try again.
        </p>
        <div class="error-actions">
            <button class="btn-primary" @click="$emit('retry')">
                Try Again
            </button>
            <button class="btn-secondary" @click="$emit('refresh')">
                Refresh Page
            </button>
        </div>
    </div>

    <div v-else-if="type === 'error-server'" class="error-state">
        <div class="error-icon">⚠️</div>
        <h3 class="error-title">Something went wrong</h3>
        <p class="error-description">
            We encountered an unexpected error. Our team has been notified and is working on a fix.
        </p>
        <div class="error-actions">
            <button class="btn-primary" @click="$emit('retry')">
                Try Again
            </button>
            <button class="btn-secondary" @click="$emit('go-home')">
                Go Home
            </button>
        </div>
    </div>

    <div v-else-if="type === 'error-not-found'" class="error-state">
        <div class="error-icon">🔍</div>
        <h3 class="error-title">Page not found</h3>
        <p class="error-description">
            The page you're looking for doesn't exist or has been moved.
        </p>
        <div class="error-actions">
            <button class="btn-primary" @click="$emit('go-home')">
                Go Home
            </button>
            <button class="btn-secondary" @click="$emit('go-back')">
                Go Back
            </button>
        </div>
    </div>

    <div v-else-if="type === 'error-permission'" class="error-state">
        <div class="error-icon">🔒</div>
        <h3 class="error-title">Access denied</h3>
        <p class="error-description">
            You don't have permission to access this resource. Please log in or contact support.
        </p>
        <div class="error-actions">
            <button class="btn-primary" @click="$emit('login')">
                Log In
            </button>
            <button class="btn-secondary" @click="$emit('go-home')">
                Go Home
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
interface Props {
    type:
    | 'skeleton-card'
    | 'skeleton-list'
    | 'skeleton-table'
    | 'skeleton-form'
    | 'empty-recipes'
    | 'empty-meals'
    | 'empty-shopping-lists'
    | 'empty-ingredients'
    | 'empty-search'
    | 'error-network'
    | 'error-server'
    | 'error-not-found'
    | 'error-permission'
    count?: number
}

withDefaults(defineProps<Props>(), {
    count: 3
})

defineEmits<{
    action: []
    retry: []
    refresh: []
    'go-home': []
    'go-back': []
    'clear-search': []
    login: []
}>()
</script>

<style scoped>
/* Skeleton Loaders */
.skeleton-card {
    background-color: white;
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-sm);
    border: 1px solid rgb(243 244 246);
    padding: 0;
    overflow: hidden;
}

.skeleton-image {
    width: 100%;
    height: 12rem;
}

.skeleton-content {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.skeleton-title {
    height: 1.5rem;
    width: 75%;
}

.skeleton-text {
    height: 1rem;
    width: 100%;
}

.skeleton-text.short {
    width: 50%;
}

.skeleton-button {
    height: 2.5rem;
    width: 8rem;
    border-radius: var(--radius-lg);
}

.skeleton-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.skeleton-list-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem;
}

.skeleton-list-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.skeleton-table {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.skeleton-table-header,
.skeleton-table-row {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 1rem;
    padding: 1rem;
}

.skeleton-form {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.skeleton-form-field {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.skeleton-label {
    height: 1rem;
    width: 6rem;
}

.skeleton-input {
    height: 3rem;
    width: 100%;
    border-radius: var(--radius-lg);
}

.skeleton-button-group {
    display: flex;
    gap: 0.75rem;
    padding-top: 1rem;
}

.skeleton-button.secondary {
    background-color: rgb(243 244 246);
}

/* Empty States */
.empty-state {
    text-align: center;
    padding: 4rem 2rem;
}

.empty-icon {
    font-size: 4rem;
    margin-bottom: 1.5rem;
}

.empty-title {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-bold);
    color: rgb(55 65 81);
    margin-bottom: 1rem;
}

.empty-description {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-normal);
    color: rgb(75 85 99);
    margin-bottom: 2rem;
    max-width: 28rem;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.625;
}

/* Error States */
.error-state {
    text-align: center;
    padding: 4rem 2rem;
}

.error-icon {
    font-size: 4rem;
    margin-bottom: 1.5rem;
}

.error-title {
    font-size: var(--font-size-xl);
    font-weight: var(--font-weight-bold);
    color: rgb(220 38 38);
    margin-bottom: 1rem;
}

.error-description {
    font-size: var(--font-size-base);
    font-weight: var(--font-weight-normal);
    color: rgb(75 85 99);
    margin-bottom: 2rem;
    max-width: 28rem;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.625;
}

.error-actions {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    justify-content: center;
}

/* Enhanced skeleton animations */
@keyframes shimmer {
    0% {
        background-position: -200px 0;
    }

    100% {
        background-position: calc(200px + 100%) 0;
    }
}

.skeleton,
.skeleton-text,
.skeleton-avatar,
.skeleton-button,
.skeleton-input {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200px 100%;
    animation: shimmer 1.5s infinite;
}

/* Green accent for skeleton loaders */
.skeleton-card .skeleton-button,
.skeleton-form .skeleton-button:first-child {
    background: linear-gradient(90deg,
            rgba(76, 175, 80, 0.1) 25%,
            rgba(76, 175, 80, 0.2) 50%,
            rgba(76, 175, 80, 0.1) 75%);
    background-size: 200px 100%;
    animation: shimmer 1.5s infinite;
}

/* Responsive adjustments */
@media (max-width: 640px) {

    .empty-state,
    .error-state {
        padding: 3rem 1rem;
    }

    .empty-icon,
    .error-icon {
        font-size: 3rem;
        margin-bottom: 1rem;
    }

    .error-actions {
        flex-direction: column;
    }

    .skeleton-table-header,
    .skeleton-table-row {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (min-width: 640px) {
    .error-actions {
        flex-direction: row;
    }
}
</style>